/**
 * ProductionMethodRoutingSection — per-location default print technology picker.
 *
 * Phase 50.01.5 (DTF Integration). Sets the MIDDLE tier of the garment-unit
 * print_technology resolution chain: SKU.print_technology → THIS location
 * default → 'DTG'. A SKU with an explicit print_technology always wins; this
 * default only applies when the SKU is unset.
 *
 * Mirrors ShippingProviderSettingsSection's per-location picker structure
 * (reads productionSettings.getLocationSettings, one single-RPC mutation per
 * row, loading/error/empty states), but routes ALL copy through t() (en + zh).
 * MINIMAL by design — a default picker, NOT a rules-table UI: the
 * prod_method_rules smart-routing engine + its UI are deferred to 50.02.
 */
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { NoteLine } from '@/components/shared/NoteLine';
import { MapPin, Loader2, AlertTriangle, RefreshCw, Layers, Film, Droplet, ExternalLink } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';

type PrintTechnology = 'DTG' | 'DTF';

/** Icon + i18n label-key per technology — colour is never the only signal. */
const TECH_PRESENTATION: Record<PrintTechnology, { icon: typeof Droplet; labelKey: string }> = {
  DTG: { icon: Droplet, labelKey: 'methodRouting.dtg' },
  DTF: { icon: Film, labelKey: 'methodRouting.dtf' },
};

const TECH_ORDER: PrintTechnology[] = ['DTG', 'DTF'];

interface ProductionMethodRoutingSectionProps {
  /** When true, renders without the Card wrapper for embedding in accordions. */
  embedded?: boolean;
  /**
   * Console single-location mode: when set, render only this location's row
   * and omit the per-row location name (the Console section header already
   * names the location). Absent = today's full-list behavior.
   */
  locationId?: string;
}

export function ProductionMethodRoutingSection({
  embedded = false,
  locationId,
}: ProductionMethodRoutingSectionProps) {
  const { t } = useTranslation('production');
  const locationsQuery = trpc.productionSettings.getLocationSettings.useQuery();

  const setTechMutation = trpc.productionSettings.setLocationPrintTechnology.useMutation({
    onSuccess: () => {
      toast.success(t('methodRouting.updated'));
      locationsQuery.refetch();
    },
    onError: (error) => {
      toast.error(t('methodRouting.updateFailed', { error: error.message }));
    },
  });

  const isLoading = locationsQuery.isLoading;
  const hasError = locationsQuery.isError;
  const allLocations = locationsQuery.data || [];
  const locations = locationId ? allLocations.filter((l) => l.id === locationId) : allLocations;

  const content = (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{t('methodRouting.description')}</p>

      {/* Error State */}
      {hasError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>{t('methodRouting.loadFailed')}</AlertTitle>
          <AlertDescription className="flex items-center gap-2">
            {locationsQuery.error?.message || t('methodRouting.unknownError')}
            <Button variant="link" className="p-0 h-auto" onClick={() => locationsQuery.refetch()}>
              <RefreshCw className="h-3 w-3 mr-1" />
              {t('methodRouting.retry')}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-8" role="status" aria-busy="true">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground motion-reduce:animate-none" aria-hidden="true" />
          <span className="ml-2 text-muted-foreground">{t('methodRouting.loading')}</span>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !hasError && locations.length === 0 && (
        <div className="flex flex-col items-center py-8 text-center bg-muted/30 rounded-lg">
          <MapPin className="h-10 w-10 text-muted-foreground/50 mb-3" />
          <p className="font-medium">{t('methodRouting.emptyTitle')}</p>
          <p className="text-sm text-muted-foreground mb-4">{t('methodRouting.emptyDesc')}</p>
          <Button variant="outline" asChild className="h-12 gap-2">
            <a href="/settings?tab=locations">
              <ExternalLink className="h-4 w-4" />
              {t('methodRouting.goToSettings')}
            </a>
          </Button>
        </div>
      )}

      {/* Location List */}
      {!isLoading && !hasError && locations.length > 0 && (
        <div className="space-y-3" role="list" aria-label={t('methodRouting.listLabel')}>
          {locations.map((location) => {
            const tech: PrintTechnology = location.printTechnology === 'DTF' ? 'DTF' : 'DTG';
            const presentation = TECH_PRESENTATION[tech];
            const TechIcon = presentation.icon;
            const rowPending =
              setTechMutation.isPending && setTechMutation.variables?.locationId === location.id;
            return (
              <div key={location.id} role="listitem" className="overflow-hidden rounded-lg bg-muted/30">
                <div className="flex items-center justify-between p-3 transition-colors hover:bg-muted/50">
                  <div>
                    {!locationId && <p className="font-medium">{location.name}</p>}
                    <NoteLine icon={TechIcon} className="mt-1">
                      {t(presentation.labelKey)}
                    </NoteLine>
                  </div>

                  <div className="flex items-center gap-2">
                    {rowPending && (
                      <Loader2
                        className="h-3 w-3 animate-spin text-muted-foreground motion-reduce:animate-none"
                        aria-hidden="true"
                      />
                    )}
                    <Select
                      value={tech}
                      onValueChange={(v) =>
                        setTechMutation.mutate({ locationId: location.id, printTechnology: v as PrintTechnology })
                      }
                      disabled={rowPending}
                    >
                      <SelectTrigger
                        className="h-8 w-[200px] text-sm"
                        aria-label={t('methodRouting.selectAria', { location: location.name })}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TECH_ORDER.map((id) => {
                          const p = TECH_PRESENTATION[id];
                          const Icon = p.icon;
                          return (
                            <SelectItem key={id} value={id} textValue={t(p.labelKey)}>
                              <div className="flex min-w-0 items-center gap-2">
                                <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                                <span className="truncate">{t(p.labelKey)}</span>
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Note: explicit per-SKU values override this default */}
      {!isLoading && !hasError && locations.length > 0 && (
        <p className="pt-2 text-xs text-muted-foreground">{t('methodRouting.overrideNote')}</p>
      )}

      {/* ARIA live region */}
      <div role="status" aria-live="polite" className="sr-only">
        {setTechMutation.isSuccess && t('methodRouting.updated')}
      </div>
    </div>
  );

  if (embedded) return content;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Layers className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
          <CardTitle className="text-lg">{t('methodRouting.title')}</CardTitle>
        </div>
        <CardDescription>{t('methodRouting.cardDescription')}</CardDescription>
      </CardHeader>
      <CardContent>{content}</CardContent>
    </Card>
  );
}
